import { LANGUAGE_OPTIONS } from "./languages";

/** Default UI language code used by LanguageDropdown. */
export const DEFAULT_UI_LANGUAGE = LANGUAGE_OPTIONS[0]?.code || "en";

/**
 * Default code stored in preferences.ai_language (API still accepts codes).
 * Action `target_language` should use the English name instead — see
 * toAiLanguageName / DEFAULT_AI_LANGUAGE_NAME.
 */
export const DEFAULT_AI_LANGUAGE = "en-US";

/** Preferred default for action `target_language` (LLM-friendly name). */
export const DEFAULT_AI_LANGUAGE_NAME = "English";

/**
 * UI dropdown codes → AI preference language codes (ai_language).
 * Doc examples: en-US, zh-CN, ja.
 */
const UI_TO_AI_LANGUAGE = {
  en: "en-US",
  el: "el",
  es: "es",
  ru: "ru",
  th: "th",
  tr: "tr",
  zcn: "zh-CN",
  ztw: "zh-TW",
  ja: "ja",
  ko: "ko",
  my: "my",
  hi: "hi",
  vi: "vi",
  fil: "fil",
};

/**
 * UI dropdown codes → English language names for action `target_language`.
 * Backend recommends names over codes (e.g. "Filipino" vs "fil") for the LLM.
 */
const UI_TO_AI_LANGUAGE_NAME = {
  en: "English",
  el: "Greek",
  es: "Spanish",
  ru: "Russian",
  th: "Thai",
  tr: "Turkish",
  zcn: "Simplified Chinese",
  ztw: "Traditional Chinese",
  ja: "Japanese",
  ko: "Korean",
  my: "Burmese",
  hi: "Hindi",
  vi: "Vietnamese",
  fil: "Filipino",
};

const AI_TO_UI_LANGUAGE = Object.fromEntries(
  Object.entries(UI_TO_AI_LANGUAGE).map(([ui, ai]) => [ai.toLowerCase(), ui]),
);

// Also accept bare regionless forms that may come back from the API.
AI_TO_UI_LANGUAGE.en = "en";
AI_TO_UI_LANGUAGE["zh-cn"] = "zcn";
AI_TO_UI_LANGUAGE["zh-tw"] = "ztw";
AI_TO_UI_LANGUAGE.zh = "zcn";

const AI_NAME_TO_UI_LANGUAGE = Object.fromEntries(
  Object.entries(UI_TO_AI_LANGUAGE_NAME).map(([ui, name]) => [
    name.toLowerCase(),
    ui,
  ]),
);

// Common aliases the API / LLM side may echo back.
AI_NAME_TO_UI_LANGUAGE.chinese = "zcn";
AI_NAME_TO_UI_LANGUAGE["chinese (simplified)"] = "zcn";
AI_NAME_TO_UI_LANGUAGE["chinese (traditional)"] = "ztw";
AI_NAME_TO_UI_LANGUAGE.myanmar = "my";
AI_NAME_TO_UI_LANGUAGE.tagalog = "fil";

/**
 * Map a UI LanguageDropdown code to an AI preference language code.
 * @param {string} uiCode
 * @returns {string}
 */
export const toAiLanguage = (uiCode) => {
  if (!uiCode || typeof uiCode !== "string") return DEFAULT_AI_LANGUAGE;
  const normalized = uiCode.trim().toLowerCase();
  if (UI_TO_AI_LANGUAGE[normalized]) return UI_TO_AI_LANGUAGE[normalized];
  // Already an AI-style code (e.g. en-US) — pass through if known reverse.
  if (AI_TO_UI_LANGUAGE[normalized]) {
    return UI_TO_AI_LANGUAGE[AI_TO_UI_LANGUAGE[normalized]] || uiCode.trim();
  }
  // English name → code (prefs may be round-tripped as names).
  if (AI_NAME_TO_UI_LANGUAGE[normalized]) {
    return UI_TO_AI_LANGUAGE[AI_NAME_TO_UI_LANGUAGE[normalized]];
  }
  return DEFAULT_AI_LANGUAGE;
};

/**
 * Map a UI code, AI code, or language name to the English name used for
 * action `target_language` (e.g. "fil" / "Filipino" → "Filipino").
 * @param {string} value
 * @returns {string}
 */
export const toAiLanguageName = (value) => {
  if (!value || typeof value !== "string") return DEFAULT_AI_LANGUAGE_NAME;
  const trimmed = value.trim();
  if (!trimmed) return DEFAULT_AI_LANGUAGE_NAME;

  const normalized = trimmed.toLowerCase();

  if (UI_TO_AI_LANGUAGE_NAME[normalized]) {
    return UI_TO_AI_LANGUAGE_NAME[normalized];
  }
  if (AI_NAME_TO_UI_LANGUAGE[normalized]) {
    return UI_TO_AI_LANGUAGE_NAME[AI_NAME_TO_UI_LANGUAGE[normalized]];
  }
  if (AI_TO_UI_LANGUAGE[normalized]) {
    return (
      UI_TO_AI_LANGUAGE_NAME[AI_TO_UI_LANGUAGE[normalized]] ||
      DEFAULT_AI_LANGUAGE_NAME
    );
  }
  const primary = normalized.split("-")[0];
  if (AI_TO_UI_LANGUAGE[primary]) {
    return (
      UI_TO_AI_LANGUAGE_NAME[AI_TO_UI_LANGUAGE[primary]] ||
      DEFAULT_AI_LANGUAGE_NAME
    );
  }
  if (UI_TO_AI_LANGUAGE_NAME[primary]) {
    return UI_TO_AI_LANGUAGE_NAME[primary];
  }

  return DEFAULT_AI_LANGUAGE_NAME;
};

/**
 * Map an AI API language code or name to a UI LanguageDropdown code.
 * @param {string} aiCode
 * @returns {string}
 */
export const toUiLanguage = (aiCode) => {
  if (!aiCode || typeof aiCode !== "string") return DEFAULT_UI_LANGUAGE;
  const normalized = aiCode.trim().toLowerCase();
  if (AI_TO_UI_LANGUAGE[normalized]) return AI_TO_UI_LANGUAGE[normalized];
  if (AI_NAME_TO_UI_LANGUAGE[normalized]) {
    return AI_NAME_TO_UI_LANGUAGE[normalized];
  }
  // Exact UI code already.
  if (UI_TO_AI_LANGUAGE[normalized]) return normalized;
  // Try primary subtag (e.g. en from en-GB).
  const primary = normalized.split("-")[0];
  if (AI_TO_UI_LANGUAGE[primary]) return AI_TO_UI_LANGUAGE[primary];
  if (UI_TO_AI_LANGUAGE[primary]) return primary;
  return DEFAULT_UI_LANGUAGE;
};
